/**
 * Binds the manifest's store keys to the five real zustand stores.
 *
 * This is the ONLY file that knows a store key maps to a live store, which is
 * what keeps `buildSettingsBundle` / `applySettingsBundle` pure and testable.
 * A key with no entry here is simply skipped — an imported bundle naming a
 * store this build dropped degrades to "nothing to apply for that store".
 *
 * Export reads LIVE state (`getState()`), not the `localStorage` envelope: the
 * live state is always populated, whereas a store that has never changed on a
 * fresh profile has no envelope yet. `collectStoreSlice` only copies manifest-
 * declared fields, so the store's actions and non-persisted fields cannot ride
 * along even though they are present on the live object.
 */
import { useAppStore } from '../../../store/appStore';
import { useSidebarStore } from '../../database-objects/store/sidebarStore';
import { useTerminalStore } from '../../terminal/store/terminalStore';
import { useSqlSnippetsStore } from '../../sql-snippets/store/sqlSnippetsStore';
import { useKeybindingsStore } from '../../keybindings/store/keybindingsStore';
import type { StoreTarget } from '../model/applySettingsBundle';

type PersistedStore = {
  getState: () => Record<string, unknown>;
  persist: { getOptions: () => { version?: number }; rehydrate: () => Promise<void> | void };
};

const STORES: Record<string, PersistedStore> = {
  'sql-studio-app-store': useAppStore as unknown as PersistedStore,
  'sql-studio-sidebar': useSidebarStore as unknown as PersistedStore,
  'sql-studio-terminal': useTerminalStore as unknown as PersistedStore,
  'sql-studio-sql-snippets': useSqlSnippetsStore as unknown as PersistedStore,
  'jam-sql-keybindings': useKeybindingsStore as unknown as PersistedStore,
};

export function readStoreState(storeKey: string): Record<string, unknown> | null {
  return STORES[storeKey]?.getState() ?? null;
}

export function storeTarget(storeKey: string): StoreTarget | undefined {
  const store = STORES[storeKey];
  if (!store || typeof localStorage === 'undefined') return undefined;
  return {
    read: () => localStorage.getItem(storeKey),
    write: (raw) => localStorage.setItem(storeKey, raw),
    remove: () => localStorage.removeItem(storeKey),
    version: () => store.persist.getOptions().version ?? 0,
    rehydrate: () => store.persist.rehydrate(),
  };
}
